/**
 * El campo del PIN: lo primero que hace un alumno.
 *
 * Es la misma pieza en dos sitios —el panel de quien entra como alumnado y `/join`— porque son la
 * misma acción. Antes el panel del alumno era un título y una frase, **sin un solo enlace para
 * llegar a jugar**: la pantalla del PIN existía y funcionaba, pero no había forma de alcanzarla.
 *
 * **Un solo campo para las cuatro cifras, no cuatro cajas.** Las cuatro cajas se ven bonitas y son
 * hostiles: obligan a gestionar el foco a mano, rompen el pegado desde el portapapeles y un lector
 * de pantalla anuncia cuatro campos sin nombre en lugar de uno con nombre.
 *
 * Dos cosas que se hacían mal y se veían en la primera pantalla del producto:
 *
 * 1. **El tamaño.** Usaba el mismo token que el PIN proyectado —`clamp(80px, 13vw, 260px)`—, así
 *    que en un portátil de 1440 px el campo pedía cifras de 187 px de alto y la caja ocupaba media
 *    pantalla para escribir cuatro dígitos. Ahora usa `--text-pin`, acotado al ancho de un móvil,
 *    que es donde de verdad se teclea; el token grande se queda para la pared.
 * 2. **El error saltaba al perder el foco.** Con el campo aún vacío, tabular fuera ya pintaba «El
 *    código son cuatro cifras» y empujaba el botón hacia abajo, justo debajo del pulgar. Ahora
 *    solo se comprueba al enviar, y lo cuenta un aviso al pie que no mueve nada de sitio.
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { isValidPin } from '@clica/shared';
import { useAvisos } from './avisos.js';
import { Boton } from './boton.js';
import { Rotulo } from './rotulo.js';

export function EntradaPin(): React.JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const avisos = useAvisos();
  const [pin, setPin] = useState('');
  /** Solo se pone al enviar algo que no vale. Escribir lo quita. */
  const [invalido, setInvalido] = useState(false);
  /**
   * Un contador que sube con cada rechazo, para poder repetir la sacudida.
   *
   * Con solo el booleano, dos envíos seguidos igual de malos no reiniciarían la animación: React
   * reutiliza el nodo y el navegador no vuelve a arrancar un `animation` que ya corrió.
   */
  const [rechazos, setRechazos] = useState(0);

  return (
    <form
      className="flex w-full flex-col gap-5"
      onSubmit={(evento) => {
        evento.preventDefault();
        if (isValidPin(pin)) {
          setInvalido(false);
          void navigate(`/play/${pin}`);
          return;
        }
        setInvalido(true);
        setRechazos((n) => n + 1);
        avisos.fallo(t('game.join.invalid'));
      }}
    >
      <div className="flex flex-col gap-2">
        <Rotulo htmlFor="pin">{t('game.join.pinLabel')}</Rotulo>
        <input
          // La `key` reinicia la sacudida en cada rechazo. Sin ella, el segundo intento fallido
          // no se acusa: el nodo es el mismo y la animación ya corrió.
          key={rechazos}
          id="pin"
          // `inputMode` saca el teclado numérico en el móvil sin impedir pegar el código.
          inputMode="numeric"
          autoComplete="off"
          autoFocus
          maxLength={4}
          placeholder="0000"
          value={pin}
          onChange={(evento) => {
            setPin(evento.target.value.replace(/\D/g, '').slice(0, 4));
            setInvalido(false);
          }}
          aria-describedby="pin-ayuda"
          aria-invalid={invalido}
          /*
           * Va en la tipografía de titulares y no en monoespaciada: Space Grotesk tiene el uno con
           * pie y el cero ancho, y a este tamaño se lee mejor. `tabular-nums` mantiene el ancho de
           * cada cifra para que el campo no baile mientras se teclea.
           *
           * El `-me-[0.18em]` cancela el espacio que `letter-spacing` deja colgando después de la
           * última cifra: sin él, cuatro dígitos centrados aparecen medio carácter a la izquierda.
           */
          className={[
            'text-pin font-display rounded-tile bg-surface w-full border-2 text-center font-bold',
            'ease-hard -me-[0.18em] py-4 tracking-[0.18em] tabular-nums',
            'transition-colors duration-(--dur-state)',
            // La sacudida es un rechazo que se entiende sin leer, y solo mueve `translateX`: no
            // toca la opacidad de nada con texto, así que no puede dejar el contraste a medias.
            invalido ? 'border-ko sacude' : 'border-line',
          ].join(' ')}
        />
        <p id="pin-ayuda" className="text-micro text-text-dim">
          {t('game.join.pinHelp')}
        </p>
      </div>

      <Boton type="submit" variante="primaria" ancho className="text-lead py-4">
        {t('game.join.submit')}
      </Boton>
    </form>
  );
}
